import {announce} from '@react-aria/live-announcer'
import type {DateValue} from 'react-aria-components'
import {usePrevious} from 'react-use'

import ArrowRightSmallSvg from '@/assets/icons/arrow-right-small.svg?react'
import ChevronRightSvg from '@/assets/icons/chevron-right.svg?react'
import Loading from '@/components/Loading/Loading'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {FlightResultItem} from '@/lib/flight/flightModel'
import {searchFlights} from '@/lib/flight/flightService'
import {BookingType, SortBy, StopsFilter, TravelClass} from '@/lib/flight/types'
import {type TimesFilter} from '@/lib/flight/types'
import {dateValueToDate, dateValueToDateValueString} from '@/lib/flight/utils'
import {useLocale} from '@/lib/locale/locale'
import {FlightDetailsRoute, FlightRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

import FilterAirlines from './components/filters/FilterAirlines'
import FilterBags from './components/filters/FilterBags'
import FilterDuration from './components/filters/FilterDuration'
import FilterPrice from './components/filters/FilterPrice'
import FilterStops from './components/filters/FilterStops'
import FilterTimes from './components/filters/FilterTimes'
import FlightSorter from './components/FlightSorter'
import style from './SearchResult.module.scss'

interface FlightLegProps {
  segment: FlightResultItem
}

const FlightLeg = ({segment}: FlightLegProps) => {
  const firstFlight = segment.flights[0]
  const lastFlight = segment.flights[segment.flights.length - 1]
  const numOfStops = segment.flights.length - 1
  const nameOfStops = segment.flights
    .slice(0, -1)
    .map(flight => flight.arrival_airport.id)
    .join(' - ')

  const travelTime = `${Math.floor(segment.total_duration / 60)}h ${segment.total_duration % 60}m`

  return (
    <div className={style.FlightLeg}>
      <div className={style.FirstLine}>
        <div className={style.Airline}>
          <VisuallyHidden as='h5'>Airline: {firstFlight?.airline}</VisuallyHidden>
          <img src={segment.airline_logo} alt='' />
        </div>
        <div className={style.Flight}>
          <div className={style.FlightStop}>
            <div className={style.FlightStopTime}>
              <VisuallyHidden as='h5'>Departure time:</VisuallyHidden>
              {new Date(firstFlight?.departure_airport.time ?? '').toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            <div className={style.FlightStopCode}>
              <VisuallyHidden as='h5'>Departure stop code: </VisuallyHidden>
              {firstFlight?.departure_airport.id}
            </div>
            <div className={style.FlightStopName}>
              <VisuallyHidden as='h5'>Departure airport name: </VisuallyHidden>
              {firstFlight?.departure_airport.name}
            </div>
          </div>
          <div className={style.FlightStopArrow}>
            <div className={style.FlightStopArrowContent}>
              <div className={style.FlightTravelTimeContainer}>
                <div className={style.FlightTravelTime}>
                  <VisuallyHidden as='h5'>Travel-time: </VisuallyHidden> {travelTime}
                </div>
              </div>
              <div className={style.FlightStopArrowIcon}>
                <div className={style.FlightStopArrowIconLine} />
                <ArrowRightSmallSvg />
              </div>
              <div className={style.FlightStopsInfoContainer}>
                <div className={style.FlightStopsInfo}>
                  <VisuallyHidden as='h5'>Stops: </VisuallyHidden>
                  {numOfStops > 0 ? (
                    <>
                      <div
                        className={style.FlightStopsCount}
                      >{`${numOfStops} stop${numOfStops > 1 ? 's' : ''}`}</div>
                      <div className={style.FlightStopsName}>{nameOfStops}</div>
                    </>
                  ) : (
                    <div className={style.FlightStopsCount}>Non-stop</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={clsx(style.FlightStop, style.End)}>
            <div className={style.FlightStopTime}>
              <VisuallyHidden as='h5'>Arrival time:</VisuallyHidden>
              {new Date(lastFlight?.arrival_airport.time ?? '').toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            <div className={style.FlightStopCode}>
              <VisuallyHidden as='h5'>Arrival stop code: </VisuallyHidden>
              {lastFlight?.arrival_airport.id}
            </div>
            <div className={style.FlightStopName}>
              <VisuallyHidden as='h5'>Arrival airport name: </VisuallyHidden>
              {lastFlight?.arrival_airport.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PreviousSegmentProps {
  segment: FlightResultItem
}

const PreviousSegment = ({segment}: PreviousSegmentProps) => {
  const firstFlight = segment.flights[0]
  const lastFlight = segment.flights[segment.flights.length - 1]
  const numOfStops = segment.flights.length - 1
  const nameOfStops = segment.flights
    .slice(0, -1)
    .map(flight => flight.arrival_airport.id)
    .join(' - ')

  const travelTime = `${Math.floor(segment.total_duration / 60)}h ${segment.total_duration % 60}m`
  const departureTime = new Date(firstFlight?.departure_airport.time ?? '').toLocaleString(
    'en-US',
    {
      hour: '2-digit',
      minute: '2-digit',
    },
  )

  const arrivalTime = new Date(lastFlight?.arrival_airport.time ?? '').toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <li className={style.PreviousSegment}>
      <div className={style.Airline}>
        <VisuallyHidden as='h5'>Airline: {firstFlight?.airline}</VisuallyHidden>
        <img src={segment.airline_logo} alt='' />
      </div>
      <div className={style.Segment}>
        <div className={style.SegmentTime}>
          <VisuallyHidden as='h5'>Departure time:</VisuallyHidden>
          {departureTime}&nbsp;-&nbsp;
          <VisuallyHidden as='h5'>Arrival time:</VisuallyHidden>
          {arrivalTime}
        </div>
        <div className={style.SegmentDetails}>
          <div>
            <VisuallyHidden as='h5'>Departure stop code: </VisuallyHidden>
            {firstFlight?.departure_airport.id}
            <VisuallyHidden as='h5'>
              Departure airport name: {firstFlight?.departure_airport.name}
            </VisuallyHidden>
            &nbsp;-&nbsp;
            <VisuallyHidden as='h5'>Arrival stop code: </VisuallyHidden>
            {lastFlight?.arrival_airport.id}
            <VisuallyHidden as='h5'>
              Arrival airport name:{lastFlight?.arrival_airport.name}{' '}
            </VisuallyHidden>
          </div>
          <div>
            <VisuallyHidden as='h5'>Stops: </VisuallyHidden>
            {numOfStops > 0 ? (
              <>
                <div
                  className={style.FlightStopsCount}
                >{`${numOfStops} stop${numOfStops > 1 ? 's' : ''}`}</div>
                <div className={style.FlightStopsName}>{nameOfStops}</div>
              </>
            ) : (
              <div className={style.FlightStopsCount}>Non-stop</div>
            )}
          </div>
          <div>
            <VisuallyHidden as='h5'>Travel-time: </VisuallyHidden> {travelTime}
          </div>
        </div>
      </div>
    </li>
  )
}

interface FlightItemProps {
  flightResultItem: FlightResultItem
  index: number
  searchParams: {
    readonly current: FlightSearchParams
  }
}

const FlightItem = ({flightResultItem, index, searchParams}: FlightItemProps) => {
  const latestFlightResultItem = useLatest(flightResultItem)
  const navigate = useNavigate()

  const book = useCallback(() => {
    const token = latestFlightResultItem.current.departure_token
    const id = latestFlightResultItem.current.flight_id

    if (token) {
      void navigate({
        to: FlightRoute.fullPath,
        search: {
          type: searchParams.current.bookingType,
          class: searchParams.current.ticketClass,
          pax: {
            adult: searchParams.current.numOfAdult,
            children: searchParams.current.numOfChildren,
            infantsInSeat: searchParams.current.numOfInfantsInSeat,
            infantsOnLap: searchParams.current.numOfInfantsOnLap,
          },
          departureDate: searchParams.current.departureDate
            ? dateValueToDateValueString(searchParams.current.departureDate)
            : undefined,
          arrivalDate: searchParams.current.arrivalDate
            ? dateValueToDateValueString(searchParams.current.arrivalDate)
            : undefined,
          departureAirport: searchParams.current.departureAirport ?? undefined,
          arrivalAirport: searchParams.current.arrivalAirport ?? undefined,
          token,
        },
        resetScroll: true,
      })
    } else if (id) {
      void navigate({
        to: FlightDetailsRoute.fullPath,
        params: {
          flightId: id,
        },
      })
    }
  }, [navigate, searchParams])

  return (
    <div role='region' aria-label={`Flight ${index + 1}`} style={{display: 'contents'}}>
      <VisuallyHidden as='h4'>
        Flight {index + 1} (price:{' '}
        {<Price price={flightResultItem.price} options={{fractionDigits: 0}} />})
      </VisuallyHidden>
      <div style={{display: 'contents'}}>
        <li className={style.FlightItem}>
          <div className={style.FlightInformation}>
            {/* <div className={style.FlightTags}>
              <div>Best</div>
              <div>Cheapest</div>
            </div> */}
            <div className={style.FlightLegs}>
              <FlightLeg key={index} segment={flightResultItem} />
            </div>
          </div>
          <div className={style.FlightDetails}>
            {/* <div className={style.FlightExtensions}>
                <div>Cabin Bag: 1</div>
                <div>Check-in Bag: 1</div>
              </div> */}
            <div className={style.FlightSummary}>
              <div>
                <div className={style.FlightPrice}>
                  <VisuallyHidden as='h5'>Price:</VisuallyHidden>
                  <Price price={flightResultItem.price} options={{fractionDigits: 0}} />
                </div>
                <div className={style.FlightTravelClass}>
                  <VisuallyHidden as='h5'>Travel class:</VisuallyHidden>{' '}
                  {flightResultItem.flights[0]?.travel_class}
                </div>
              </div>
              <PrimaryButton className={style.BookNow} onPress={book}>
                Select flight
                <VisuallyHidden>
                  <span style={{whiteSpace: 'pre'}}>{index + 1}</span>
                </VisuallyHidden>
              </PrimaryButton>
            </div>
          </div>
        </li>
      </div>
    </div>
  )
}

// interface MinimalFlightInfo {

// }

interface FlightSearchParams {
  bookingType?: BookingType | undefined
  ticketClass?: TravelClass | undefined
  numOfAdult?: number | undefined
  numOfChildren?: number | undefined
  numOfInfantsInSeat?: number | undefined
  numOfInfantsOnLap?: number | undefined
  departureDate?: DateValue | null
  arrivalDate?: DateValue | null
  departureAirport?: string | null | undefined
  arrivalAirport?: string | null | undefined
  token?: string | undefined
  // previousFlights: MinimalFlightInfo[]
}

interface FlightFilterParams {
  stops?: StopsFilter
  bags?: number
  maxPrice?: number | null
  times?: TimesFilter[] | null
  maxDuration?: number | null
  includedAirlines?: string[]
}

const isFlightMatchFilter = (
  flight: FlightResultItem,
  index: number,
  filterParams: FlightFilterParams,
) => {
  if (filterParams.maxPrice && flight.price > filterParams.maxPrice) return false

  if (filterParams.maxDuration && flight.total_duration > filterParams.maxDuration * 60)
    return false

  if (
    filterParams.includedAirlines &&
    filterParams.includedAirlines.length > 0 &&
    !filterParams.includedAirlines.includes(flight.flights[0]?.airline ?? '')
  )
    return false

  if (filterParams.times) {
    const departureTimeString = flight.flights[0]?.departure_airport.time
    if (departureTimeString) {
      const departureTime = new Date(departureTimeString)

      let hourDeparture = departureTime.getHours()
      if (departureTime.getMinutes() > 0) {
        hourDeparture += 1
      }

      if (
        hourDeparture < (filterParams.times[index]?.departure.from ?? 0) ||
        hourDeparture > (filterParams.times[index]?.departure.to ?? 24)
      )
        return false
    }
    const arrivalTimeString = flight.flights[flight.flights.length - 1]?.arrival_airport.time

    if (arrivalTimeString) {
      const arrivalTime = new Date(arrivalTimeString)

      let hourArrival = arrivalTime.getHours()
      if (arrivalTime.getMinutes() > 0) {
        hourArrival += 1
      }

      if (
        hourArrival < (filterParams.times[index]?.arrival.from ?? 0) ||
        hourArrival > (filterParams.times[index]?.arrival.to ?? 24)
      )
        return false
    }
  }

  if (filterParams.stops) {
    const numOfStops = flight.flights.length - 1

    const cases = {
      [StopsFilter.NonStop]: numOfStops === 0,
      [StopsFilter.OneStop]: numOfStops <= 1,
      [StopsFilter.TwoStops]: numOfStops <= 2,
      [StopsFilter.Any]: numOfStops >= 0,
    }

    if (!cases[filterParams.stops]) return false
  }

  return true
}

export interface FlightSearchResultControl {
  search: (params: FlightSearchParams) => void
}

interface Props {
  ref: Ref<FlightSearchResultControl>
}

export default memo<Props>(function SearchResult({ref}) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [searchParams, setSearchParams] = useState<FlightSearchParams>({})
  const latestSearchParams = useLatest(searchParams)
  const [locale] = useLocale()
  // const askRef = useRef<AskInnoSearchControl>(null)
  const isMobile = useIsMobile()
  const [filterParams, setFilterParams] = useState<FlightFilterParams>({})

  // ---------------------------------------------------------------------------

  const key =
    String(searchParams.bookingType) +
    String(searchParams.ticketClass) +
    String(searchParams.numOfAdult) +
    String(searchParams.numOfChildren) +
    String(searchParams.numOfInfantsInSeat) +
    String(searchParams.numOfInfantsOnLap) +
    String(searchParams.departureDate) +
    String(searchParams.arrivalDate) +
    String(searchParams.departureAirport) +
    String(searchParams.arrivalAirport) +
    String(searchParams.token)

  const previousKey = usePrevious(key)

  const isKeyChanged = previousKey !== key

  console.log(isKeyChanged, key, previousKey)

  const {
    data: flightResults,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      'flightSearch',
      locale,
      searchParams,
      filterParams.stops ?? StopsFilter.Any,
      filterParams.bags,
    ],
    queryFn:
      searchParams.departureAirport && searchParams.arrivalAirport
        ? async () =>
            searchFlights({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- checked before
              from: searchParams.departureAirport!,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- checked before
              to: searchParams.arrivalAirport!,
              departureDate: searchParams.departureDate
                ? new Date(searchParams.departureDate.toString())
                : new Date(),
              returnDate: searchParams.arrivalDate
                ? new Date(searchParams.arrivalDate.toString())
                : undefined,
              mode: searchParams.bookingType,
              travelClass: searchParams.ticketClass,
              adults: searchParams.numOfAdult,
              children: searchParams.numOfChildren,
              infantsInSeat: searchParams.numOfInfantsInSeat,
              infantsOnLap: searchParams.numOfInfantsOnLap,
              locale,
              token: searchParams.token,
              filter: {
                // stops: filterParams.stops ?? StopsFilter.Any,
                bags: filterParams.bags ?? 0,
                // maxPrice: filterParams.maxPrice,
                // times: filterParams.times,
                // maxDuration: filterParams.maxDuration,
                // includedAirlines: filterParams.includedAirlines,
              },
            })
        : skipToken,
    placeholderData: previousData => {
      if (!isKeyChanged) return previousData
      return undefined
    },
    ...NO_REFETCH_OPTIONS,
  })

  // ---------------------------------------------------------------------------

  const [stops, setStops] = useState<StopsFilter>(StopsFilter.Any)
  const latestStops = useLatest(stops)
  const [bags, setBags] = useState(0)
  const latestBags = useLatest(bags)
  const [maxPrice, setMaxPrice] = useState<number | null>(null)
  const latestMaxPrice = useLatest(maxPrice)
  const [times, setTimes] = useState<TimesFilter[] | null>(null)
  const latestTimes = useLatest(times)
  const [maxDuration, setMaxDuration] = useState<number | null>(null) // in minutes
  const latestMaxDuration = useLatest(maxDuration)
  const [includedAirlines, setIncludedAirlines] = useState<string[]>([])
  const latestIncludedAirlines = useLatest(includedAirlines)
  const [sorter, setSorter] = useState<SortBy>(SortBy.Price)

  const maximumPrice = (() => {
    if (!flightResults) return 100
    return Math.ceil(
      [...flightResults.flights.best_flights, ...flightResults.flights.other_flights].reduce(
        (max, flight) => {
          return Math.max(max, flight.price)
        },
        0,
      ),
    )
  })()

  const minimumPrice = (() => {
    if (!flightResults) return 0
    const minimum = Math.ceil(
      Math.min(
        ...[...flightResults.flights.best_flights, ...flightResults.flights.other_flights].map(
          flight => flight.price,
        ),
      ),
    )
    if (minimum === maximumPrice) return maximumPrice - 1
    return minimum
  })()

  const maximumDuration = (() => {
    if (!flightResults) return 28
    return Math.ceil(
      [...flightResults.flights.best_flights, ...flightResults.flights.other_flights].reduce(
        (max, flight) => {
          return Math.max(max, flight.total_duration)
        },
        0,
      ) / 60,
    )
  })()

  const minimumDuration = (() => {
    if (!flightResults) return 0
    const minimum = Math.ceil(
      Math.min(
        ...[...flightResults.flights.best_flights, ...flightResults.flights.other_flights].map(
          flight => flight.total_duration,
        ),
      ) / 60,
    )

    if (minimum === maximumDuration) return maximumDuration - 1
    return minimum
  })()

  const airlines = (() => {
    if (!flightResults) return []
    return Array.from(
      new Set(
        [...flightResults.flights.best_flights, ...flightResults.flights.other_flights].flatMap(
          flight => flight.flights.map(mFlight => mFlight.airline),
        ),
      ),
    )
  })()

  const applyFilters = useCallback(() => {
    announce('Applying filters', 'polite')
    setFilterParams({
      stops: latestStops.current,
      bags: latestBags.current,
      maxPrice: latestMaxPrice.current,
      times: latestTimes.current,
      maxDuration: latestMaxDuration.current,
      includedAirlines: latestIncludedAirlines.current,
    })
  }, [])

  const resetFilters = useCallback(() => {
    setFilterParams({})
  }, [])

  // ---------------------------------------------------------------------------

  const filteredAndSortedFlightResults = useMemo(() => {
    if (!flightResults) return flightResults

    const sortFn = (flight1: FlightResultItem, flight2: FlightResultItem) => {
      switch (sorter) {
        case SortBy.Price:
          return flight1.price - flight2.price
        case SortBy.DepartureTime:
          return (flight1.flights[0]?.departure_airport.time ?? '').localeCompare(
            flight2.flights[0]?.departure_airport.time ?? '',
          )
        case SortBy.ArrivalTime:
          return (
            flight1.flights[flight1.flights.length - 1]?.arrival_airport.time ?? ''
          ).localeCompare(flight2.flights[flight2.flights.length - 1]?.arrival_airport.time ?? '')
        case SortBy.Duration:
          return flight1.total_duration - flight2.total_duration
      }
    }

    const filteredBestFlights = flightResults.flights.best_flights
      .filter(flight =>
        isFlightMatchFilter(flight, flightResults.previous_segments?.length ?? 0, filterParams),
      )
      .sort(sortFn)

    const filteredOtherFlights = flightResults.flights.other_flights
      .filter(flight =>
        isFlightMatchFilter(flight, flightResults.previous_segments?.length ?? 0, filterParams),
      )
      .sort(sortFn)

    return {
      ...flightResults,
      flights: {
        best_flights: filteredBestFlights,
        other_flights: filteredOtherFlights,
      },
    }
  }, [flightResults, filterParams, sorter])

  // ---------------------------------------------------------------------------

  const search = useCallback(
    (searchParams: FlightSearchParams) => {
      resetFilters()
      setSearchParams(searchParams)
      if (!anchorRef.current) return
      anchorRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
    },
    [resetFilters],
  )

  useImperativeHandle(ref, () => {
    return {
      search,
    }
  }, [search])

  // ---------------------------------------------------------------------------

  const numberOfFlightsUnfiltered = flightResults
    ? flightResults.flights.best_flights.length + flightResults.flights.other_flights.length
    : 0

  const numberOfFlights = filteredAndSortedFlightResults
    ? filteredAndSortedFlightResults.flights.best_flights.length +
      filteredAndSortedFlightResults.flights.other_flights.length
    : 0
  const noFlight = numberOfFlights === 0

  const headingText = (() => {
    if (searchParams.bookingType === BookingType.OneWay) {
      return 'Results'
    }

    if (searchParams.bookingType === BookingType.RoundTrip) {
      if (searchParams.token) {
        invariant(searchParams.arrivalDate)
        return `Return flights ${dateValueToDate(searchParams.arrivalDate).toLocaleDateString(
          'en-US',
          {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          },
        )}`
      }

      invariant(searchParams.departureDate)
      return `Departure flights ${dateValueToDate(searchParams.departureDate).toLocaleDateString(
        'en-US',
        {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        },
      )}`
    }

    if (searchParams.bookingType === BookingType.MultiCity) {
      return 'Multi-city flights'
    }

    return 'Flights'
  })()

  return (
    <>
      <div className={style.Container}>
        <div
          ref={anchorRef}
          style={{position: 'absolute', top: isMobile ? '-60px' : '-80px', left: 0}}
        />
        {isLoading && <Loading label='Searching for flights...' />}
        {flightResults && (
          <>
            {numberOfFlightsUnfiltered > 5 && (
              <div className={style.Filters}>
                <h2>Filters</h2>
                <FilterStops stops={stops} setStops={setStops} />
                <FilterBags carryOnBags={bags} onCarryOnBagsChange={setBags} />
                <FilterPrice
                  maxPrice={maxPrice}
                  setMaxPrice={setMaxPrice}
                  minimumPrice={minimumPrice}
                  maximumPrice={maximumPrice}
                />
                <FilterDuration
                  maxDuration={maxDuration}
                  setMaxDuration={setMaxDuration}
                  minimumDuration={minimumDuration}
                  maximumDuration={maximumDuration}
                />
                {!!searchParams.bookingType && (
                  <FilterTimes
                    bookingType={searchParams.bookingType}
                    times={times}
                    setTimes={setTimes}
                  />
                )}
                <FilterAirlines
                  airlines={airlines}
                  includedAirlines={includedAirlines}
                  onIncludedAirlinesChange={setIncludedAirlines}
                />
                <PrimaryButton size='sm' onPress={applyFilters}>
                  {isFetching ? (
                    'Applying... '
                  ) : (
                    <span>
                      Apply<VisuallyHidden> &nbsp;</VisuallyHidden>
                    </span>
                  )}
                  <VisuallyHidden>filters</VisuallyHidden>
                </PrimaryButton>
              </div>
            )}
            <div className={style.Heading}>
              <h2>{headingText}</h2>
              <FlightSorter sorter={sorter} setSorter={setSorter} />
            </div>
            {flightResults.previous_segments && flightResults.previous_segments.length > 0 && (
              <div className={style.SelectedFlights}>
                <VisuallyHidden as='h3'>Selected flights</VisuallyHidden>
                <ol className={style.PreviousSegments}>
                  {flightResults.previous_segments.map((flightResultItem, index) => (
                    <PreviousSegment key={index} segment={flightResultItem} />
                  ))}
                </ol>
                <div className={style.ChooseReturnFlights}>
                  {' '}
                  <ChevronRightSvg /> Choose return flights
                </div>
              </div>
            )}
            {noFlight ? (
              <div role='alert'>No flights found</div>
            ) : (
              <>
                <VisuallyHidden as='div' role='alert'>
                  <VisuallyHidden as='span'>Found {numberOfFlights} flights</VisuallyHidden>
                </VisuallyHidden>
                {filteredAndSortedFlightResults &&
                  filteredAndSortedFlightResults.flights.best_flights.length > 0 && (
                    <>
                      <h3>Best flights</h3>
                      <ol className={style.FlightItemContainer}>
                        {filteredAndSortedFlightResults.flights.best_flights.map(
                          (flightResultItem, index) => (
                            <FlightItem
                              searchParams={latestSearchParams}
                              key={flightResultItem.flight_id ?? flightResultItem.departure_token}
                              index={index}
                              flightResultItem={flightResultItem}
                            />
                          ),
                        )}
                      </ol>
                    </>
                  )}
                {filteredAndSortedFlightResults &&
                  filteredAndSortedFlightResults.flights.other_flights.length > 0 && (
                    <>
                      <h3>Other flights</h3>
                      <ol className={style.FlightItemContainer}>
                        {filteredAndSortedFlightResults.flights.other_flights.map(
                          (flightResultItem, index) => (
                            <FlightItem
                              searchParams={latestSearchParams}
                              key={flightResultItem.flight_id ?? flightResultItem.departure_token}
                              index={index}
                              flightResultItem={flightResultItem}
                            />
                          ),
                        )}
                      </ol>
                    </>
                  )}
              </>
            )}
          </>
        )}
      </div>
      {/* {flightResults?.metadata['thread-id'] && (
        <AskInnoSearch
          threadId={flightResults.metadata['thread-id']}
          prompts={flightResults.suggested_prompts}
          ref={askRef}
        />
      )} */}
    </>
  )
})
